/**
 * 4-cue oscillator preset, generated via Web Audio API. No licensed samples.
 * Per Decision 8 in the plan, all four cues sit at a barely-perceptible level —
 * felt more than heard. Reinforces the Fin-Game-is-restraint principle.
 *
 *   stateChange      → 200 Hz blip, 80 ms decay   (every CDM FSM transition)
 *   moduleUnlock     → rising minor third, 240 ms (every dashboard module unlock)
 *   pillarTransition → low pad, 600 ms swell      (Verify→Trace, Trace→Finance)
 *   indeterminate    → descending half-step, 320 ms (Vega INDETERMINATE moment only)
 */

export type Cue = "stateChange" | "moduleUnlock" | "pillarTransition" | "indeterminate";

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  // Some browsers gate AudioContext until user gesture; if creation throws,
  // we silently no-op and try again next time.
  try {
    ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    return ctx;
  } catch {
    return null;
  }
}

function tone(freq: number, durationMs: number, type: OscillatorType = "sine", peakGain = 0.04) {
  const a = getCtx();
  if (!a) return;
  const osc = a.createOscillator();
  const gain = a.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.value = 0;
  osc.connect(gain).connect(a.destination);
  const now = a.currentTime;
  gain.gain.linearRampToValueAtTime(peakGain, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
  osc.start(now);
  osc.stop(now + durationMs / 1000 + 0.05);
}

export function play(cue: Cue) {
  switch (cue) {
    case "stateChange":
      tone(200, 80, "sine", 0.04);
      return;
    case "moduleUnlock":
      tone(330, 100, "triangle", 0.05);
      setTimeout(() => tone(392, 140, "triangle", 0.05), 80);
      return;
    case "pillarTransition":
      tone(110, 600, "sine", 0.05);
      return;
    case "indeterminate":
      tone(380, 160, "sine", 0.05);
      setTimeout(() => tone(360, 160, "sine", 0.05), 140);
      return;
  }
}
